
import React, { useState } from 'react';
import { Modal, Upload, Select, Button, message, Alert } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import axios from 'axios';

const { Dragger } = Upload;
const { Option } = Select;

interface UploadModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ visible, onClose, onSuccess }) => {
    const [sourceType, setSourceType] = useState<string>('NEWS');
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState<any[]>([]);

    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.error('请先选择文件');
            return;
        }

        const formData = new FormData();
        formData.append('file', fileList[0]);
        formData.append('source_type', sourceType);

        setUploading(true);
        try {
            // Assuming API base URL is configured in axios or via proxy
            // Using relative path for now, adjust if needed
            await axios.post('/api/v1/intelligence/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            message.success('文件上传主要并分析完成');
            setFileList([]);
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Upload failed:', error);
            message.error(error.response?.data?.detail || '上传失败，请重试');
        } finally {
            setUploading(false);
        }
    };

    const props: UploadProps = {
        onRemove: (file) => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
        },
        beforeUpload: (file) => {
            const isPDF = file.type === 'application/pdf';
            const isText = file.type === 'text/plain' || file.name.endsWith('.md');

            if (!isPDF && !isText) {
                message.error('只支持 PDF, TXT, Markdown 文件');
                return Upload.LIST_IGNORE;
            }

            setFileList([file]); // Limit to 1 file
            return false;
        },
        fileList,
    };

    return (
        <Modal
            title="上传情报文件"
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="back" onClick={onClose}>
                    取消
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={uploading}
                    onClick={handleUpload}
                    disabled={fileList.length === 0}
                >
                    开始分析
                </Button>,
            ]}
        >
            <div style={{ marginBottom: 16 }}>
                <Alert
                    message="支持 PDF, TXT, Markdown 文件"
                    description="系统将自动提取文本并通过 AI 进行结构化分析。"
                    type="info"
                    showIcon
                />
            </div>

            <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>情报类型：</div>
                <Select
                    value={sourceType}
                    onChange={setSourceType}
                    style={{ width: '100%' }}
                >
                    <Option value="NEWS">新闻资讯 (News)</Option>
                    <Option value="TENDER">招标公告 (Tender)</Option>
                    <Option value="TRADE">贸易数据 (Trade)</Option>
                    <Option value="SUPPLY_DEMAND">供需信息 (Supply/Demand)</Option>
                    <Option value="REPORT">储能报告 (Report)</Option>
                </Select>
            </div>

            <Dragger {...props}>
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                <p className="ant-upload-hint">
                    支持单文件上传。严禁上传敏感数据。
                </p>
            </Dragger>
        </Modal>
    );
};

export default UploadModal;
